import { createAjv } from './ajv-factory';
import { mergeTwo } from './merge';

const MAX_ITERATIONS = 6;

function isObject(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function getConditionals(schema) {
  if (!isObject(schema) || !Array.isArray(schema.allOf)) return [];
  return schema.allOf.filter((m) => isObject(m) && isObject(m.if));
}

/** Effective schema = base (allOf conditionals stripped) + folded then/else branches. */
function buildEffective(rootSchema, foldedBranches) {
  const base = { ...rootSchema };
  if (Array.isArray(base.allOf)) {
    const plain = base.allOf.filter((m) => !(isObject(m) && isObject(m.if)));
    if (plain.length > 0) base.allOf = plain; else delete base.allOf;
  }
  return foldedBranches.reduce((acc, branch) => mergeTwo(acc, branch), base);
}

/**
 * Resolve if/then/else conditionals by actively triggering THEN branches.
 *
 * Strategy:
 *  1. Proactive seeding — for each conditional, generate values that satisfy its
 *     `if` (reusing generateValue); if they don't conflict with already-forced
 *     fields, force them and fold the `then` branch. Conflicts are recorded.
 *  2. Stabilize — a bounded fixpoint that folds the matching then/else for every
 *     conditional whose `if` currently evaluates true/false (AJV = source of
 *     truth), catching accidental triggers and chained rules.
 *
 * @param {object} rootSchema - preprocessed root schema (x-allOf already → allOf)
 * @param {(schema: object) => *} generateValue - recursive value generator
 * @returns {{ effectiveSchema: object, forced: object, triggered: number[], unmetTriggers: number[] }}
 */
export function resolveConditionals(rootSchema, generateValue) {
  const conditionals = getConditionals(rootSchema);
  if (conditionals.length === 0) {
    return { effectiveSchema: rootSchema, forced: {}, triggered: [], unmetTriggers: [] };
  }

  const ajv = createAjv();
  const forced = {};
  const foldedBranches = [];
  const triggered = [];
  const unmetTriggers = [];

  // 1. Proactive seeding
  conditionals.forEach((cond, idx) => {
    const ifValues = generateValue(cond.if);
    if (!isObject(ifValues)) return;
    const conflict = Object.keys(ifValues).some(
      (k) => k in forced && JSON.stringify(forced[k]) !== JSON.stringify(ifValues[k]),
    );
    if (conflict) {
      unmetTriggers.push(idx);
      return;
    }
    Object.assign(forced, ifValues);
    if (isObject(cond.then)) {
      foldedBranches.push(cond.then);
      triggered.push(idx);
    }
  });

  // 2. Stabilize
  for (let iter = 0; iter < MAX_ITERATIONS; iter += 1) {
    const effective = buildEffective(rootSchema, foldedBranches);
    const payload = { ...generateValue(effective), ...forced };
    let changed = false;
    conditionals.forEach((cond) => {
      let ifTrue;
      try { ifTrue = ajv.validate(cond.if, payload); } catch { ifTrue = false; }
      if (ifTrue && isObject(cond.then) && !foldedBranches.includes(cond.then)) {
        foldedBranches.push(cond.then);
        changed = true;
      }
      if (!ifTrue && isObject(cond.else) && !foldedBranches.includes(cond.else)) {
        foldedBranches.push(cond.else);
        changed = true;
      }
    });
    if (!changed) break;
  }

  return {
    effectiveSchema: buildEffective(rootSchema, foldedBranches),
    forced,
    triggered,
    unmetTriggers,
  };
}
